import { backend, isLocalMode } from '../data/backend.js';
import { toast } from './toast.js';

/**
 * Konto-Dialog. Mit PocketBase meldet man sich mit Name und Passwort an, Konten legt
 * nur der Administrator an. Ohne Server bleibt es beim lokalen Profil: das trennt
 * Favoriten pro Name, aber nur auf diesem Gerät.
 */
export function createAccount({ button, onChange }) {
  const el = (id) => document.getElementById(id);
  const dialog = el('account-dialog');
  const form = el('account-form');
  const title = el('account-title');
  const note = el('account-note');
  const idLabel = el('account-id-label');
  const idInput = el('account-id');
  const idField = idInput.closest('.field');
  const pwField = el('account-pw-field');
  const pwLabel = el('account-pw-label');
  const pwInput = el('account-pw');
  const pw2Field = el('account-pw2-field');
  const pw2Input = el('account-pw2');
  const errorEl = el('account-error');
  const cancelBtn = el('account-cancel');
  const absenden = form.querySelector('button[type="submit"]');

  let profile = null;
  let ansicht = 'anmelden'; // oder 'konto'

  // Abmelden steht abseits: es ist keine Einstellung und soll nicht versehentlich
  // statt „Speichern“ getroffen werden.
  const abmelden = document.createElement('button');
  abmelden.type = 'button';
  abmelden.className = 'linkbtn';
  abmelden.style.marginRight = 'auto';
  abmelden.textContent = 'Abmelden';
  abmelden.addEventListener('click', async () => {
    dialog.close();
    await logout();
  });
  document.querySelector('.dialog__actions')?.prepend(abmelden);

  function paintButton() {
    const label = profile ? profile.name : (isLocalMode ? 'Profil' : 'Anmelden');
    const text = button.querySelector('.navbtn__text');
    if (text) text.textContent = label;
    else button.textContent = label;
    button.title = profile ? `Konto von ${profile.name}` : (isLocalMode ? 'Lokales Profil anlegen' : 'Anmelden');
    button.setAttribute('aria-label', button.title);
  }

  /**
   * Zwei Gesichter. Das Konto zeigt gleich, was man dort ändern kann - Name und
   * Passwort stehen offen da und nicht hinter einem weiteren Klick.
   */
  function zeigen(welche) {
    ansicht = welche;
    errorEl.hidden = true;
    pwInput.value = '';
    pw2Input.value = '';
    const konto = welche === 'konto';
    const amServer = konto && profile?.server;

    idField.hidden = konto && !amServer;
    idInput.required = !idField.hidden;
    idInput.value = amServer ? profile.name : '';
    idInput.autocomplete = konto ? 'nickname' : 'username';
    pwField.hidden = konto ? !amServer : isLocalMode;
    pwInput.required = !konto && !isLocalMode;
    pw2Field.hidden = !amServer;
    pw2Input.required = false;
    abmelden.hidden = !konto;
    absenden.hidden = konto && !amServer;

    if (konto) {
      title.textContent = 'Konto';
      note.textContent = amServer
        ? `Angemeldet als ${profile.kennung}. Favoriten, Touren und Einstellungen sieht nur dieses Konto. Das Passwort bleibt, wenn die Felder leer sind.`
        : `Lokales Profil ${profile.name}: alles liegt nur in diesem Browser.`;
      idLabel.textContent = 'Anzeigename';
      pwLabel.textContent = 'Aktuelles Passwort';
      absenden.textContent = 'Speichern';
    } else {
      title.textContent = isLocalMode ? 'Lokales Profil' : 'Anmelden';
      note.textContent = isLocalMode
        ? 'Ein Name trennt Favoriten und Touren, wenn mehrere dieses Gerät nutzen. Alles bleibt in diesem Browser.'
        : 'Favoriten, Touren und Einstellungen liegen dann im Konto, auf jedem Gerät dieselben. Was bisher im Browser lag, wird übernommen.';
      idLabel.textContent = isLocalMode ? 'Profilname' : 'Name';
      pwLabel.textContent = 'Passwort';
      absenden.textContent = 'Weiter';
    }
    if (!dialog.open) dialog.showModal();
    (amServer ? pwInput : (konto ? abmelden : idInput)).focus();
  }

  /** Name und Passwort im Konto: gespeichert wird nur, was sich geändert hat. */
  async function kontoSpeichern() {
    const name = idInput.value.trim();
    const neu = pw2Input.value;
    const alt = pwInput.value;
    if (Boolean(neu) !== Boolean(alt)) {
      throw new Error(neu ? 'Bitte auch das aktuelle Passwort angeben.' : 'Bitte auch das neue Passwort angeben.');
    }
    const meldungen = [];
    // Zuerst das Passwort: schlägt es fehl, ist noch nichts halb gespeichert.
    if (neu) {
      profile = await backend.auth.passwortAendern(profile, alt, neu);
      meldungen.push('Passwort geändert.');
    }
    if (name && name !== profile.name) {
      profile = await backend.auth.nameAendern(profile, name);
      meldungen.push('Name geändert.');
      paintButton();
      // Die Favoriten nennen das Konto im Kopf ihrer Spalte.
      onChange?.(profile);
    }
    dialog.close();
    toast(meldungen.join(' ') || 'Nichts geändert.');
  }

  async function restore() {
    try {
      profile = await backend.auth.getSession();
    } catch {
      profile = null;
    }
    paintButton();
    onChange?.(profile);
    return profile;
  }

  async function logout() {
    await backend.auth.logout();
    profile = null;
    paintButton();
    onChange?.(profile);
    toast('Abgemeldet.');
  }

  /** Was noch aus der Zeit vor dem Konto im Browser liegt. */
  async function bestandUebernehmen() {
    let z = null;
    try {
      z = await backend.uebernehmen(profile);
    } catch {
      toast('Nicht alles liess sich übernehmen. Beim nächsten Anmelden geht es weiter.', 5000);
      return;
    }
    if (!z || !(z.favoriten + z.touren)) {
      toast(`Angemeldet als ${profile.name}.`);
      return;
    }
    const teile = [
      z.favoriten ? `${z.favoriten} ${z.favoriten === 1 ? 'Favorit' : 'Favoriten'}` : null,
      z.touren ? `${z.touren} ${z.touren === 1 ? 'Tour' : 'Touren'}` : null
    ].filter(Boolean).join(' und ');
    toast(`Angemeldet als ${profile.name}. ${teile} ins Konto übernommen.`, 5000);
  }

  button.addEventListener('click', () => zeigen(profile ? 'konto' : 'anmelden'));
  cancelBtn.addEventListener('click', () => dialog.close());

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.hidden = true;
    absenden.disabled = true;
    try {
      if (ansicht === 'konto') {
        await kontoSpeichern();
      } else {
        profile = await backend.auth.login(idInput.value, pwInput.value);
        if (!profile) throw new Error('Anmeldung fehlgeschlagen.');
        dialog.close();
        paintButton();
        await bestandUebernehmen();
        onChange?.(profile);
      }
    } catch (err) {
      errorEl.textContent = err.message || 'Hat nicht geklappt.';
      errorEl.hidden = false;
    } finally {
      absenden.disabled = false;
    }
  });

  return { restore, getProfile: () => profile, logout };
}
